import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    ViewChild,
} from '@angular/core';
import { AppService } from '../../app.service';
import { catchError, finalize, of, tap } from 'rxjs';
import { isArrayWithData } from '../../utilities/array.helpers';
import { CampaignPerformanceOverview } from './interfaces/campaign-performance-overview.interface';
import { CampaignPerformanceService } from '../../services/campaign-performance/campaign-performance.service';
import { CreditorCampaignPerformance } from './interfaces/creditor-campaign-performance.interface';
import { CreditorCampaignPerformanceListItem } from './interfaces/creditor-campaign-performance-list-item.interface';
import { simplifyDateToString } from '../../utilities/date.helpers';

@Component({
    selector: 'app-campaign-trackers',
    templateUrl: './campaign-trackers.component.html',
    styleUrl: './campaign-trackers.component.css',
})
export class CampaignTrackersComponent {
    constructor(
        private appService: AppService,
        private apiService: CampaignPerformanceService,
        private cdr: ChangeDetectorRef,
    ) {}

    date: Date = new Date();

    @ViewChild('tabsContainer') tabsContainer!: ElementRef<HTMLDivElement>;

    isShowOverview: boolean = true;
    isShowTotals: boolean = true;
    creditorOverviews: CampaignPerformanceOverview[] = [];

    activeTabId!: number;
    activeTabIndex!: number;
    creditorPerformanceItemsAll: CreditorCampaignPerformanceListItem[] = [];
    creditorPerformanceTotalItemsAll: CreditorCampaignPerformanceListItem[] =
        [];

    _activeCreditorTabPerformanceList!: CreditorCampaignPerformance[];
    _activeCreditorTabPerformanceTotalList!: CreditorCampaignPerformance[];

    tagColors: { [tag: string]: string } = {};
    tagColorsTotal: { [tag: string]: string } = {};

    toggleOptions: any[] = [
        {
            icon: 'pi pi-arrow-down-left-and-arrow-up-right-to-center',
            tooltip: 'Collapse to show totals only',
            isShowDetails: false,
        },
        {
            icon: 'pi pi-arrows-v',
            tooltip: 'Expand to show breakdown of details',
            isShowDetails: true,
        },
    ];
    selectedToggle: any = this.toggleOptions[0];

    ngOnInit() {
        this.getOverviews();
    }

    ngAfterViewInit() {
        // optional: scroll active tab into view initially
        this.scrollToActiveTab();
    }

    getOverviews = () => {
        this.creditorOverviews = [];
        this.creditorPerformanceItemsAll = [];
        this.appService.isShowPageSpinner = true;
        this.apiService
            .getOverviews(simplifyDateToString(this.date))
            .pipe(
                tap((res: CampaignPerformanceOverview[]) => {
                    if (!!res && isArrayWithData(res)) {
                        this.creditorOverviews = res;
                        const selectedOverview = this.creditorOverviews[0];
                        this.activeTabId = selectedOverview.CreditorId;

                        this.fetchFromApiTheActiveCreditorTabCampaignPerformance();
                    }
                }),
                catchError(this.appService.handleError),
                finalize(() => {
                    this.appService.isShowPageSpinner = false;
                }),
            )
            .subscribe();
    };

    get activeCreditorTabPerformanceList(): CreditorCampaignPerformance[] {
        return this._activeCreditorTabPerformanceList;
    }

    set activeCreditorTabPerformanceList(value: CreditorCampaignPerformance[]) {
        const sorted = this.sortItemsAlphabetically(value);
        const withColors = this.assignTagColors(sorted);
        this._activeCreditorTabPerformanceList = withColors;
    }

    get activeCreditorTabPerformanceTotalList(): CreditorCampaignPerformance[] {
        return this._activeCreditorTabPerformanceTotalList;
    }

    set activeCreditorTabPerformanceTotalList(
        value: CreditorCampaignPerformance[],
    ) {
        const sorted = this.sortItemsAlphabetically(value);
        const withColors = this.assignTagColorsTotal(sorted);
        this._activeCreditorTabPerformanceTotalList = withColors;
    }

    sortItemsAlphabetically(performanceList: CreditorCampaignPerformance[]) {
        let newPerformanceList = [...performanceList];
        newPerformanceList?.sort((a: any, b: any) =>
            a.AutomationTag.localeCompare(b.AutomationTag),
        );

        return newPerformanceList;
    }

    assignTagColors(performanceList: CreditorCampaignPerformance[]) {
        const newPerformanceList = [...performanceList];

        const uniqueTags = Array.from(
            new Set(newPerformanceList.map((x) => x.AutomationTag)),
        );

        this.tagColors = {};

        uniqueTags.forEach((tag, index) => {
            const total = uniqueTags.length;
            const hue = Math.round((index / total) * 360);
            this.tagColors[tag] = `hsl(${hue}, 70%, 50%)`;
        });

        return newPerformanceList;
    }

    assignTagColorsTotal(performanceList: CreditorCampaignPerformance[]) {
        const newPerformanceList = [...performanceList];

        const uniqueTags = Array.from(
            new Set(newPerformanceList.map((x) => x.AutomationTag)),
        );

        this.tagColorsTotal = {};

        uniqueTags.forEach((tag, index) => {
            const total = uniqueTags.length;
            const hue = Math.round((index / total) * 360);
            this.tagColorsTotal[tag] = `hsl(${hue}, 70%, 50%)`;
        });

        return newPerformanceList;
    }

    fetchFromApiTheActiveCreditorTabCampaignPerformance = (
        foundCreditorItem?: CreditorCampaignPerformanceListItem,
        foundCreditorItemTotal?: CreditorCampaignPerformanceListItem,
    ) => {
        this.appService.isShowPageSpinner = true;

        let spinnerCount = 0;

        const hideSpinner = () => {
            spinnerCount -= 1;
            if (spinnerCount === 0) {
                this.appService.isShowPageSpinner = false;
            }
        };

        if (!foundCreditorItem) {
            spinnerCount += 1;
            this.apiService
                .getCampaignPerformanceByCreditorId(
                    this.activeTabId,
                    simplifyDateToString(this.date),
                )
                .pipe(
                    tap((res: CreditorCampaignPerformance[]) => {
                        this.creditorPerformanceItemsAll = [
                            ...this.creditorPerformanceItemsAll,
                            {
                                CreditorId: this.activeTabId,
                                List: res,
                            } as CreditorCampaignPerformanceListItem,
                        ];

                        this.activeCreditorTabPerformanceList = res;
                        this.cdr.detectChanges();
                    }),
                    catchError(this.appService.handleError),
                    finalize(() => {
                        hideSpinner();
                    }),
                )
                .subscribe();
        } else {
            this.activeCreditorTabPerformanceList = foundCreditorItem.List;
        }

        if (!foundCreditorItemTotal) {
            spinnerCount += 1;
            this.apiService
                .getCampaignPerformanceTotalByCreditorId(
                    this.activeTabId,
                    simplifyDateToString(this.date),
                )
                .pipe(
                    tap((res: CreditorCampaignPerformance[]) => {
                        this.creditorPerformanceTotalItemsAll = [
                            ...this.creditorPerformanceTotalItemsAll,
                            {
                                CreditorId: this.activeTabId,
                                List: res,
                            } as CreditorCampaignPerformanceListItem,
                        ];

                        this.activeCreditorTabPerformanceTotalList = res;
                        this.cdr.detectChanges();
                    }),
                    catchError(this.appService.handleError),
                    finalize(() => {
                        hideSpinner();
                    }),
                )
                .subscribe();
        } else {
            this.activeCreditorTabPerformanceTotalList =
                foundCreditorItemTotal.List;
        }
    };

    get hasTableData(): boolean {
        return isArrayWithData(this.creditorOverviews);
    }

    toggleOverview() {
        this.isShowOverview = !this.isShowOverview;
    }

    // Convert string to HSL color
    stringToColor(str: string): string {
        // Rainbow-like base hues (degrees on HSL color wheel)
        const baseHues = [210, 0, 60, 120, 300, 30, 180, 270];
        // 210 = blue, 0 = red, 60 = yellow, 120 = green, etc.

        // deterministic hash
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }

        const hue = baseHues[Math.abs(hash) % baseHues.length];
        const s = 65 + (Math.abs(hash) % 20); // saturation 65-85%
        const l = 50; // lightness

        return `hsl(${hue}, ${s}%, ${l}%)`;
    }

    getMockData() {
        this.creditorOverviews = Array.from(
            { length: 100 },
            (_, i) =>
                ({
                    CreditorId: i + 1,
                    CreditorName: 'Creditor ' + i,
                    RecipientCount: 12000,
                    RejectedEmails: 1000,
                    RejectedSMS: 1000,
                }) as CampaignPerformanceOverview,
        );
        this.activeTabId = this.creditorOverviews[0].CreditorId;

        const creditorPerformanceItems = [
            {
                AutomationTag: '60-89dpd',
                CommunicationChannels: 'Email',
                Name: 'John',
                DaysFromStart: 3,
                EligibleAccounts: 10,
                RejectedEmails: 1,
                RejectedSMS: 0,
                FinalRecipientCount: 9,
            },
            {
                AutomationTag: '60-89dpd',
                CommunicationChannels: 'SMS',
                Name: 'Mary',
                DaysFromStart: 5,
                EligibleAccounts: 15,
                RejectedEmails: 0,
                RejectedSMS: 2,
                FinalRecipientCount: 13,
            },
            {
                AutomationTag: '90+dpd',
                CommunicationChannels: 'Email',
                Name: 'Paul',
                DaysFromStart: 2,
                EligibleAccounts: 8,
                RejectedEmails: 0,
                RejectedSMS: 0,
                FinalRecipientCount: 8,
            },
            {
                AutomationTag: '90+dpd',
                CommunicationChannels: 'Email/SMS',
                Name: 'Lisa',
                DaysFromStart: 4,
                EligibleAccounts: 20,
                RejectedEmails: 2,
                RejectedSMS: 1,
                FinalRecipientCount: 17,
            },
            {
                AutomationTag: 'MWFdpd',
                CommunicationChannels: 'Email',
                Name: 'Bob',
                DaysFromStart: 1,
                EligibleAccounts: 12,
                RejectedEmails: 1,
                RejectedSMS: 0,
                FinalRecipientCount: 11,
            },
        ];

        this.creditorPerformanceItemsAll.push({
            CreditorId: this.activeTabId,
            List: creditorPerformanceItems,
        });

        console.log(this.creditorPerformanceItemsAll);
    }

    selectTab(tabId: number, index: number) {
        this.activeTabId = tabId;
        this.activeTabIndex = index;

        const foundCreditorItem = this.creditorPerformanceItemsAll.find(
            (item: CreditorCampaignPerformanceListItem) =>
                item.CreditorId === this.activeTabId,
        );

        const foundCreditorItemTotal =
            this.creditorPerformanceTotalItemsAll.find(
                (item: CreditorCampaignPerformanceListItem) =>
                    item.CreditorId === this.activeTabId,
            );

        if (!foundCreditorItem || !foundCreditorItemTotal) {
            this.fetchFromApiTheActiveCreditorTabCampaignPerformance(
                foundCreditorItem,
                foundCreditorItemTotal,
            );
        } else {
            this.activeCreditorTabPerformanceList = foundCreditorItem?.List!;
            this.activeCreditorTabPerformanceTotalList =
                foundCreditorItemTotal?.List!;
        }
    }

    scrollLeft() {
        this.tabsContainer.nativeElement.scrollBy({
            left: -1000,
            behavior: 'smooth',
        });
    }

    scrollRight() {
        this.tabsContainer.nativeElement.scrollBy({
            left: 1000,
            behavior: 'smooth',
        });
    }

    scrollToActiveTab() {
        const activeEl = this.tabsContainer?.nativeElement.querySelector(
            '.tab.active',
        ) as HTMLElement;
        if (activeEl) {
            activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center' });
        }
    }
}
